import { apiGetAuth, apiPostAuthWithConfig, apiPostAuth } from '@/utils/api'
import { API_USER_MANAGEMENT } from '@/utils/api-url'
import { notificationDanger, notificationSuccess } from '@/utils/notification'
import router from '@/router'

const state = () => ({
  applicationList: {},
  downloadedByHistory: {},
  isLoading: false,
  isUploading: false,
  isDownloading: false,
  uploadPercentage: 0,
  toggle_modal_add_edit_discard: {
    targetModals: false,
    title: '',
    description: '',
    customTitleBtnOk: '',
    customClass: '',
    type: '',
    loading: false
  }
})

const mutations = {
  SET_APPLICATION_LIST(state, payload) {
    state.applicationList = payload
  },
  SET_DOWNLOADED_BY_HISTORY(state, payload) {
    state.downloadedByHistory = payload
  },
  SET_IS_LOADING(state, payload) {
    state.isLoading = payload
  },
  SET_IS_UPLOADING(state, payload) {
    state.isUploading = payload
  },
  SET_IS_DOWNLOADING(state, payload) {
    state.isDownloading = payload
  },
  SET_UPLOAD_PERCENTAGE(state, payload) {
    state.uploadPercentage = payload
  },
  SET_TOGGLE_MODAL_ADD_EDIT_DISCARD(state, payload) {
    state.toggle_modal_add_edit_discard = payload
  }
}

const actions = {
  async fetchApplicationList({ commit }, params) {
    try {
      commit('SET_IS_LOADING', true)
      const { data } = await apiGetAuth(API_USER_MANAGEMENT.APPLICATION, params)
      commit('SET_APPLICATION_LIST', data.data)
    } catch (err) {
      notificationDanger(err.message)
      commit('SET_APPLICATION_LIST', {})
    } finally {
      commit('SET_IS_LOADING', false)
    }
  },

  async fetchApplicationDownloadedBy({ commit }, params) {
    try {
      const { data } = await apiGetAuth(API_USER_MANAGEMENT.APPLICATION + '/history', params)
      commit('SET_DOWNLOADED_BY_HISTORY', data.data)
    } catch (err) {
      notificationDanger(err.message)
      commit('SET_DOWNLOADED_BY_HISTORY', {})
    }
  },

  async uploadApplication({ commit }, payload) {
    const { file, description, version } = payload
    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', 'picker')
    formData.append('description', description)
    formData.append('version', version)

    try {
      commit('SET_IS_UPLOADING', true)
      const response = await apiPostAuthWithConfig(API_USER_MANAGEMENT.APPLICATION + '/upload', formData, {
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total)
          commit('SET_UPLOAD_PERCENTAGE', percent)
        }
      })
      notificationSuccess('Application uploaded successfully')
      router.push({ name: 'PickerAppMsi' })
      return response.data
    } catch (error) {
      notificationDanger(error.message || 'Upload failed')
      return null
    } finally {
      commit('SET_IS_UPLOADING', false)
      commit('SET_UPLOAD_PERCENTAGE', 0)
    }
  },
  
  async triggerApplicationDownload({ commit }, id) {
    commit('SET_IS_DOWNLOADING', true)
    try {
      const { data } = await apiPostAuth(API_USER_MANAGEMENT.APPLICATION + '/download/' + id)
      const downloadInfo = data.data

      if (!downloadInfo?.file_url) {
        throw new Error('Invalid download data received from API.')
      }

      const fileResponse = await fetch(downloadInfo.file_url)
      if (!fileResponse.ok) {
        throw new Error(`Failed to fetch file: ${fileResponse.statusText}`)
      }
      const fileBlob = await fileResponse.blob()

      notificationSuccess('Application downloaded successfully')
      return {
        blob: fileBlob,
        filename: downloadInfo.file_name
      }
    } catch (err) {
      notificationDanger(err.message || 'Failed to download application')
      return null
    } finally {
      commit('SET_IS_DOWNLOADING', false)
    }
  }
}

const getters = {
  getApplicationList: (state) => state.applicationList,
  getDownloadedByList: (state) => state.downloadedByHistory?.data || [],
  getDownloadedByHistoryTotal: (state) => state.downloadedByHistory?.meta?.total || 0,
  getApplicationListTotal: (state) => state.applicationList?.meta?.total || 0,
  isLoading: (state) => state.isLoading,
  isUploading: (state) => state.isUploading,
  isDownloading: (state) => state.isDownloading,
  uploadPercentage: (state) => state.uploadPercentage,
  getToggleModalAddEditDiscard: (state) => state.toggle_modal_add_edit_discard
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
